// Export for an editor document (#13261 R5): the design-system menu of
// formats (Word, PDF, HTML, the two clipboard copies), then the same
// options dialog every export goes through. One hook, so the artifact pane
// header's Export button and its "Export…" title-menu item open the one
// menu, and the full editor's toolbar opens it too.
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Menu } from "@/components/FpPages/RMS/design";
import { RootState } from "@/redux/store";
import { useWorkspaceData } from "../FpEditor/services/useWorkspaceData";
import exportToPDF from "./services/exportToPDF";
import exportToHTML from "./services/exportToHTML";
import exportToDOCX from "./services/exportToDOCX";
import copyToClipboard from "./services/copyToClipboard";
import ExportOptionsModal from "./ExportOptionsModal";
import fpLog from "../../utils/fpLog";

type ExportType =
  | "docx"
  | "pdf"
  | "html"
  | "clipboard-html"
  | "clipboard-markdown";

const FORMATS: { type: ExportType; label: string }[] = [
  { type: "docx", label: "Microsoft Word (.docx)" },
  { type: "pdf", label: "PDF Document (.pdf)" },
  { type: "html", label: "HTML Document (.html)" },
  { type: "clipboard-html", label: "Copy to Clipboard" },
  { type: "clipboard-markdown", label: "Copy as Markdown" },
];

export interface DocumentExport {
  /** Opens the format menu at `anchor`. */
  open: (anchor: HTMLElement) => void;
  /** While the document is saving, an export would miss the last edit. */
  disabled: boolean;
  /** The menu and the options dialog; render once, anywhere. */
  ui: React.ReactNode;
}

export const useDocumentExport = (): DocumentExport => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [pending, setPending] = useState<ExportType | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const workspaceId = useSelector(
    (state: RootState) => state.fpAppV4Data.workspaceId
  );
  const { data: workspaceData } = useWorkspaceData(workspaceId || undefined, {
    refetchOnMount: false,
  });
  const title = workspaceData?.title;

  useEffect(() => {
    const onSavingChange = () =>
      setSaving(window.savingWorkspaceContent || false);
    window.addEventListener("savingContentStateChange", onSavingChange);
    return () =>
      window.removeEventListener("savingContentStateChange", onSavingChange);
  }, []);

  const runExport = async (type: ExportType, includeLinks: boolean) => {
    // The live editor FpEditor publishes, read when the export runs.
    const editor = window.fp?.editor;
    if (!editor) return;
    if (type === "clipboard-html" || type === "clipboard-markdown") {
      try {
        const format = type === "clipboard-html" ? "html" : "markdown";
        await copyToClipboard(editor, format, includeLinks, workspaceId!);
        window.openFpToast("Document copied to clipboard", "success");
      } catch (error) {
        console.error("Copy to clipboard failed:", error);
        window.openFpToast("Copy to clipboard failed", "error");
      }
      return;
    }
    try {
      if (type === "pdf") {
        await exportToPDF(editor, title, includeLinks);
        fpLog("PDF exported");
      } else if (type === "html") {
        await exportToHTML(editor, title, includeLinks);
        fpLog("HTML exported");
      } else {
        await exportToDOCX(editor, workspaceId!, title, includeLinks);
        fpLog("DOCX exported");
      }
    } catch (error) {
      console.error("Export failed:", error);
      window.openFpToast("Export failed", "error");
    }
  };

  const ui = (
    <>
      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => setAnchor(null)}
        label="Export"
        items={FORMATS.map((format) => ({
          label: format.label,
          onSelect: () => {
            setAnchor(null);
            setPending(format.type);
          },
        }))}
      />
      {pending && (
        <ExportOptionsModal
          open
          onClose={() => setPending(null)}
          onExport={(includeLinks) => void runExport(pending, includeLinks)}
          exportType={pending}
        />
      )}
    </>
  );

  return { open: setAnchor, disabled: saving, ui };
};
